/**
 * List Archive — shared vocabulary for the visibility-only archive flag on the
 * Catalog Operations, Feed Audit and Experiments lists (workflow-service
 * "Archive API Integration Contract", changesets 013 attribution / 014 history).
 * Archiving hides a finished row from the default list and nothing else; it is
 * exactly reversible. See `docs/list-archive.md`.
 */

/** Server-side `?archived=` filter accepted by the jobs, status-counts and
 *  experiments list endpoints. `ACTIVE` is the default and is never sent. */
export type ArchivedFilter = 'ACTIVE' | 'ARCHIVED' | 'ALL';

/** Which side of a list an admin is looking at. */
export type ArchiveView = 'active' | 'archived';

/** Body of `PATCH …/archived` — one required boolean; `true` archives, `false` unarchives. */
export interface SetArchivedRequest {
  archived: boolean;
}

/** One visibility change on a row. `by` is `null` when no authenticated
 *  principal was in scope — e.g. a re-run cleared the flag automatically. */
export interface ArchiveHistoryEntry {
  action: 'ARCHIVED' | 'UNARCHIVED';
  at: string;
  by: string | null;
}

/** Any row carrying the contract's archive stamps (list rows, headers, details).
 *  `archivedBy` is populated only while archived; `null` on rows archived before
 *  changeset 013 — render nothing for it, never a placeholder. */
export interface ArchivableRow {
  archivedAt?: string | null | undefined;
  archivedBy?: string | null | undefined;
}

/** Archive state is derived from the timestamp — the contract deliberately has
 *  no separate boolean, so the flag and the stamp can never disagree. */
export function isArchived(row: ArchivableRow | null | undefined): boolean {
  return row?.archivedAt != null;
}

/** Maps the UI view to the wire filter. The active view yields `undefined` so
 *  the default list request stays byte-identical to today's (and keeps sharing
 *  its query-cache slot with the existence probes). */
export function toArchivedFilter(view: ArchiveView): ArchivedFilter | undefined {
  return view === 'archived' ? 'ARCHIVED' : undefined;
}

// ---------------------------------------------------------------------------
// Refusals (409) — `{ error: { code, details, fields } }` envelope
// ---------------------------------------------------------------------------

/** What the server saw when it probed the run's workflow. `OPEN` = executing;
 *  `UNKNOWN` = Temporal unreachable (precautionary refusal — never say "live");
 *  `CLOSED` / `NOT_PROBED` only appear on reconcile responses. */
export type ArchiveLiveness = 'OPEN' | 'UNKNOWN' | 'CLOSED' | 'NOT_PROBED';

// ---------------------------------------------------------------------------
// Bulk — `POST …/archived:batch`
// ---------------------------------------------------------------------------

/** Hard server cap per batch call; the request is refused above it. Selections
 *  larger than this are chunked client-side (see `runArchiveBatch`). */
export const ARCHIVE_BATCH_CAP = 100;

export interface ArchiveBatchRequest {
  /** 1–100 ids. Duplicates are collapsed server-side. */
  ids: string[];
  archived: boolean;
}

/** Per-item outcome. `UNCHANGED` is a success (idempotent repeat) but not work
 *  done; `REFUSED_LIVE` is the single-row 409; `ERROR` is retryable. */
export type ArchiveBatchOutcome = 'UPDATED' | 'UNCHANGED' | 'REFUSED_LIVE' | 'NOT_FOUND' | 'ERROR';

export interface ArchiveBatchResult {
  id: string;
  outcome: ArchiveBatchOutcome;
  archivedAt?: string | null;
  errors?: string[];
  /** Present on `REFUSED_LIVE` — the live workflow, for a Stop deep-link. */
  workflowId?: string | null;
}

export interface ArchiveBatchSummary {
  updated: number;
  unchanged: number;
  refused: number;
  notFound: number;
  errored: number;
}

/** Always `200` when the request is well-formed — even if every id failed.
 *  Read `summary` and each `results[].outcome`; never branch on the HTTP status. */
export interface ArchiveBatchResponse {
  summary: ArchiveBatchSummary;
  /** In request order, duplicate ids collapsed. */
  results: ArchiveBatchResult[];
}

// ---------------------------------------------------------------------------
// Reconcile — `POST jobs/{id}/reconcile`
// ---------------------------------------------------------------------------

/** Non-destructive repair for a row stuck at IN_PROGRESS whose run has actually
 *  finished. `reconciled: false` is a SUCCESS — `detail` explains why nothing
 *  changed (liveness OPEN = the row is telling the truth; UNKNOWN = Temporal
 *  unreachable, safe to retry; NOT_PROBED = status already settled). Stamps
 *  FAILED, never COMPLETED, and never rewrites a settled status. */
export interface ReconcileEnrichmentJobResponse {
  reconciled: boolean;
  previousStatus: string | null;
  status: string;
  liveness: ArchiveLiveness;
  probedWorkflowIds: string[];
  detail: string;
}
